import React from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "../../context/NotificationsContext";

const formatRelative = (iso) => {
  if (!iso) return "";
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return "";
  const diffMs = Date.now() - dt.getTime();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 48) return `${hr}h`;
  const day = Math.floor(hr / 24);
  return `${day}d`;
};

const NotificationRow = ({ n, onClick }) => {
  const createdAt = n?.created_at || n?.createdAt;
  const readAt = n?.read_at || n?.readAt;
  const isUnread = !readAt;
  const priority = String(n?.priority || "").toLowerCase();

  return (
    <Box
      w="100%"
      p={3}
      borderWidth="1px"
      borderColor={isUnread ? "purple.200" : "gray.200"}
      bg={isUnread ? "purple.50" : "white"}
      borderRadius="md"
      cursor="pointer"
      _hover={{ bg: isUnread ? "purple.100" : "gray.50" }}
      onClick={onClick}
    >
      <Flex justify="space-between" align="flex-start" gap={3}>
        <Box>
          <HStack spacing={2} mb={1}>
            {priority === "high" && <Badge colorScheme="red">High</Badge>}
            {isUnread ? (
              <Badge colorScheme="purple" variant="solid">
                Unread
              </Badge>
            ) : (
              <Badge colorScheme="gray" variant="subtle">
                Read
              </Badge>
            )}
          </HStack>
          <Text fontWeight="semibold" color="gray.800" noOfLines={2}>
            {n?.title || "Notification"}
          </Text>
          {!!(n?.body || n?.message) && (
            <Text fontSize="sm" color="gray.600" mt={1} noOfLines={3}>
              {n?.body || n?.message}
            </Text>
          )}
        </Box>

        <Tooltip label={createdAt ? new Date(createdAt).toLocaleString() : ""}>
          <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">
            {formatRelative(createdAt)}
          </Text>
        </Tooltip>
      </Flex>
    </Box>
  );
};

const filterUnread = (items) => (Array.isArray(items) ? items.filter((n) => !(n?.read_at || n?.readAt)) : []);

const NotificationsDrawer = () => {
  const navigate = useNavigate();
  const {
    isOpen,
    closeNotifications,
    items,
    loadingList,
    error,
    fetchNotifications,
    markRead,
    markAllRead,
    getDeepLink,
  } = useNotifications();

  const handleOpenTab = async (status) => {
    // Keep it simple: always fetch a fresh list when switching tabs
    await fetchNotifications({ status });
  };

  const handleClick = async (n) => {
    const link = getDeepLink(n);
    if (n?.id) {
      await markRead(n.id);
    }
    closeNotifications();
    if (link) navigate(link);
  };

  const allItems = Array.isArray(items) ? items : [];
  const unreadItems = filterUnread(allItems);

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={closeNotifications} size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>
          <Flex justify="space-between" align="center" gap={4} pr={10}>
            <Text>Notifications</Text>
            <Button size="sm" variant="outline" onClick={markAllRead}>
              Mark all as read
            </Button>
          </Flex>
        </DrawerHeader>
        <Divider />
        <DrawerBody>
          <Tabs
            variant="enclosed"
            onChange={(idx) => handleOpenTab(idx === 0 ? "unread" : "all")}
            isFitted
          >
            <TabList mb={3}>
              <Tab>Unread ({unreadItems.length})</Tab>
              <Tab>All ({allItems.length})</Tab>
            </TabList>

            <TabPanels>
              <TabPanel px={0}>
                {loadingList ? (
                  <Flex justify="center" py={8}>
                    <Spinner />
                  </Flex>
                ) : unreadItems.length === 0 ? (
                  <Text color="gray.600">No unread notifications.</Text>
                ) : (
                  <VStack spacing={3} align="stretch">
                    {unreadItems.map((n) => (
                      <NotificationRow key={n.id || n.created_at || Math.random()} n={n} onClick={() => handleClick(n)} />
                    ))}
                  </VStack>
                )}
              </TabPanel>

              <TabPanel px={0}>
                {loadingList ? (
                  <Flex justify="center" py={8}>
                    <Spinner />
                  </Flex>
                ) : allItems.length === 0 ? (
                  <Text color="gray.600">No notifications yet.</Text>
                ) : (
                  <VStack spacing={3} align="stretch">
                    {allItems.map((n) => (
                      <NotificationRow key={n.id || n.created_at || Math.random()} n={n} onClick={() => handleClick(n)} />
                    ))}
                  </VStack>
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>

          {error && (
            <Box mt={4} p={3} borderWidth="1px" borderColor="orange.200" bg="orange.50" borderRadius="md">
              <Text fontSize="sm" color="orange.800">
                Notifications service not available yet.
              </Text>
            </Box>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default NotificationsDrawer;

